import { useState, useContext, useEffect } from "react";
import { Card, Col, Collapse, Form, OverlayTrigger, ProgressBar, Row, Table, Tooltip } from "react-bootstrap";
import { CCBlock } from "../../../Physician/Models/CCBlock";
import { AuthContext } from "../../../Auth/AuthProvider";
import { CCAttestation, CCAttestationType } from './../../../Physician/Models/CCAttestation';
import { Button } from 'react-bootstrap';
import { Fragment } from 'react';
import { deepCopy } from "../../../Shared/Utility/DeepCopy";
import { abpnClient } from "../../../../Services/AbpnClient";
import { Alert } from 'react-bootstrap';
import { Link, Prompt } from "react-router-dom";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";
import CCActivity from "../../../Physician/Models/CCActivity";
import React from "react";
import ClinicalStatusRow from "../../Certificates/Components/ClinicalStatusRow";
import Physician from "../../../Physician/Models/Physician";
import CCBlockEvaluationTernaryRequirement from "../../../Physician/Models/CCBlockEvaluationTernaryRequirement";
import { CCAssessmentType } from "../../../Physician/Models/CCEnrollment";
import moment from "moment";


interface IProps {
    ccBlock: CCBlock;
    ccVerifiedActivities: Array<CCActivity>;
    isReadOnly: boolean;
    isPhysicianPatientSafetyRequired ?: CCBlockEvaluationTernaryRequirement;
    onSaveBlock: (updatedBlock: CCBlock) => void;
    physician: Physician;
    updatePhysician: (updatedPhysician: Physician) => void;
}

export const PatientSafetlyRequirementsColumn = (props: {isPhysicianPatientSafetyRequired : CCBlockEvaluationTernaryRequirement, ccBlock : CCBlock, isPSVerified: boolean}) => {
    const showPsCheck = CCBlockEvaluationTernaryRequirement.NotRequired !== props.isPhysicianPatientSafetyRequired && (props.ccBlock.hasPatientSafetyCompleted || props.isPSVerified);
    const showRequirementsNotMetLabel = props.isPhysicianPatientSafetyRequired !== CCBlockEvaluationTernaryRequirement.NotRequired && props.ccBlock.isPatientSafetyRequired == CCBlockEvaluationTernaryRequirement.RequirementsNotMet;
    const requirementsLabel = showRequirementsNotMetLabel ? "Not Met" : "Not Required";
    const nbsp = '\u00A0';

    return <>
        <div className="font-weight-bolder mt-2 mb-0 pp-font-size-smaller">
            <a href="https://abpn.org/maintain-certification/moc-activity-requirements/patient-safety-activity/" target="_blank">Patient Safety (PS)</a>{nbsp}
        </div>
        {showPsCheck && <div className="mb-4"><i className="font-weight-bolder las la-check pp-icon-secondary position-absolute"></i></div> }
        {!showPsCheck && <span className="pp-font-size-smaller">
            <span>{requirementsLabel}</span>
        </span>}
    </>
}

export const PatientSafetyActivityAttestationRow = (props : {isPhysicianPatientSafetyRequired : CCBlockEvaluationTernaryRequirement, ccBlock : CCBlock, isPSVerified: boolean, isPSChecked: boolean, isReadOnly : boolean, isPSDisabled : boolean, updateIsPSChecked : Function}) => {
    const showIsPatientSafetyRequired = props.isPhysicianPatientSafetyRequired !== CCBlockEvaluationTernaryRequirement.NotRequired && props.ccBlock.isPatientSafetyRequired !== CCBlockEvaluationTernaryRequirement.NotRequired;
    const showIsVerifiedLabel = props.isPhysicianPatientSafetyRequired != CCBlockEvaluationTernaryRequirement.NotRequired && props.isPSVerified;
    const showTotalResultCheck = props.isPSChecked || props.isPSVerified || props.ccBlock.hasPipWaiver;

    return <tr>
        <td>
            <Form.Label className="mb-1 pp-font-size-smaller">Patient Safety</Form.Label>
        </td>
        <td>
            {!showIsPatientSafetyRequired && <span>Not Required</span>}
            {showIsPatientSafetyRequired && showIsVerifiedLabel && <Form.Label>{"-"}</Form.Label>}
            {showIsPatientSafetyRequired && !showIsVerifiedLabel && <Form.Check type="checkbox">
                <Form.Check.Input type="checkbox" checked={props.isPSChecked} disabled={props.isReadOnly || props.isPSDisabled} onChange={() => props.updateIsPSChecked()} />
            </Form.Check>}
        </td>
        <td>
            <Form.Label>{props.isPSVerified ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
        </td>
        <td>
            <Form.Label>{"-"}</Form.Label>
        </td>
        <td>
            <Form.Label>{showTotalResultCheck ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
        </td>
    </tr>
}

export function CCActivityRequirements(props: IProps) {
    const [isPIPClinicalChecked, setIsPIPClincalChecked] = useState<boolean>(props.ccBlock.hasPipClinicalAttestation);
    const [isPIPFeedbackChecked, setIsPIPFeedbackChecked] = useState<boolean>(props.ccBlock.hasPipFeedbackAttestation);
    const [isPSChecked, setIsPSChecked] = useState<boolean>(props.ccBlock.hasPatientSafetyCompleted || props.isPhysicianPatientSafetyRequired === CCBlockEvaluationTernaryRequirement.NotRequired);
    const [isEditingAttestations, setIsEditingAttestations] = useState<boolean>(false);
    const [displaySuccessSaveAlert, setDisplaySuccessSaveAlert] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
    const [ccBlock, setCCBlock] = useState<CCBlock>(deepCopy(props.ccBlock));

    useEffect(() => {
        var block = deepCopy(props.ccBlock)
        setIsPIPClincalChecked(block.hasPipClinicalAttestation);
        setIsPIPFeedbackChecked(block.hasPipFeedbackAttestation);
        setIsPSChecked(block.hasPatientSafetyCompleted || props.isPhysicianPatientSafetyRequired === CCBlockEvaluationTernaryRequirement.NotRequired);
        setCCBlock(block);
    }, [props.ccBlock])

    const authContext = useContext(AuthContext);

    let isPSDisabled = props.isPhysicianPatientSafetyRequired === CCBlockEvaluationTernaryRequirement.NotRequired;
    let isPSVerified = props.ccVerifiedActivities.length > 0 && props.ccVerifiedActivities.filter(_ => _.isPatientSafety).length > 0;
    let isPIPClinicalVerifiedChecked = ccBlock?.ccVerifiedActivities.map(_ => _.isPipClinical).reduce((a, b) => a || b, 0);
    let isPIPFeedbackVerifiedChecked = ccBlock?.ccVerifiedActivities.map(_ => _.isPipFeedback).reduce((a, b) => a || b, 0);
    let isPipRequired = ccBlock?.clinicalStatus == 2;
    let isPIPDisabled = isPIPClinicalVerifiedChecked == true || isPIPFeedbackVerifiedChecked == true || !isPipRequired;
    let selfAttestedPIPClinicalCredits = isPIPClinicalChecked ? 1 : 0;
    let selfAttestedPIPFeedbackCredits = isPIPFeedbackChecked ? 1 : 0;
    let selfAttestedPSCredits = isPSChecked ? 1 : 0;
    let totalWaivedAndEarnedCMECredits = ccBlock.totalCMEAttestationCredits + ccBlock.totalWaivedCMECredits + ccBlock.totalVerifiedCMECredits;
    let totalWaivedAndEarnedSACredits = ccBlock.totalSAAttestationCredits + ccBlock.totalWaivedSACredits + ccBlock.totalVerifiedSACredits;
    let overageOfSACredits = totalWaivedAndEarnedSACredits > ccBlock.requiredSACredits ? totalWaivedAndEarnedSACredits - ccBlock.requiredSACredits : 0;
    let percentCMECredits = Math.round((totalWaivedAndEarnedCMECredits + overageOfSACredits > ccBlock.requiredCMECredits ? ccBlock.requiredCMECredits : totalWaivedAndEarnedCMECredits + overageOfSACredits) / ccBlock.requiredCMECredits * 100);
    let percentSACredits = Math.round((totalWaivedAndEarnedSACredits > ccBlock.requiredSACredits ? ccBlock.requiredSACredits : totalWaivedAndEarnedSACredits) / ccBlock.requiredSACredits * 100);
    let isInAbcc = ccBlock.enrollmentCertificates.filter(_ => _.ccAssessmentType == CCAssessmentType.Article).length > 0;
    let ClinicalStatusIsEditable = ccBlock.hasFailedAudit == false && ccBlock.ccBlockId == props.physician.currentCCEnrollment.currentCCBlock.ccBlockId;

    const toggleEdit = () => {
        setIsEditingAttestations(!isEditingAttestations);
    }

    const createNewAttestation = (type: CCAttestationType) => {
        var newAttestation = new CCAttestation();
        newAttestation.ccAttestationType = type;
        newAttestation.appliedToYear = ccBlock.endYear;
        return newAttestation;
    }

    const updateSelfReportedCME = (value: number) => {
        var attestation = ccBlock.ccAttestations.find(_ => _.appliedToYear === ccBlock.endYear && _.ccAttestationType === CCAttestationType.CME);

        if (attestation == null) {
            attestation = createNewAttestation(CCAttestationType.CME);
        }

        attestation.selfReportedCredits = value;
        attestation.ccBlockId = ccBlock.ccBlockId;
        updateCCAttestations(attestation);
        setIsFormDirty(true);
    }

    const updateSelfReportedSACME = (value: number) => {
        var attestation = ccBlock.ccAttestations.find(_ => _.appliedToYear === ccBlock.endYear && _.ccAttestationType === CCAttestationType.SA);

        if (attestation == null) {
            attestation = createNewAttestation(CCAttestationType.SA);
        }

        attestation.selfReportedCredits = value;
        attestation.ccBlockId = ccBlock.ccBlockId;
        updateCCAttestations(attestation);
        setIsFormDirty(true);
    }

    const updateCCAttestations = (attestation: CCAttestation) => {
        var newCCBlock = deepCopy(ccBlock);
        var newList = deepCopy(ccBlock.ccAttestations);

        if (attestation.ccAttestationType === CCAttestationType.PIPClinical) {
            var filteredPIPClinicalList = newList.filter(_ => _.ccAttestationType !== CCAttestationType.PIPFeedback);
            newList = filteredPIPClinicalList;
        }

        if (attestation.ccAttestationType === CCAttestationType.PIPFeedback) {
            var filteredPIPFeedbackList = newList.filter(_ => _.ccAttestationType !== CCAttestationType.PIPClinical);
            newList = filteredPIPFeedbackList;
        }

        var index = ccBlock.ccAttestations.findIndex(_ => _.ccAttestationId === attestation.ccAttestationId && _.ccAttestationType === attestation.ccAttestationType);

        if (index === -1) {
            newList.push(attestation);
        }
        else {
            newList[index] = attestation;
        }

        newCCBlock.ccAttestations = newList;
        if (attestation.ccAttestationType == CCAttestationType.CME) newCCBlock.totalCMEAttestationCredits = attestation.selfReportedCredits
        if (attestation.ccAttestationType == CCAttestationType.SA) newCCBlock.totalSAAttestationCredits = attestation.selfReportedCredits
        setCCBlock(newCCBlock);
    }

    const saveAllCCAttestations = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        e.currentTarget.classList.add("was-validated");
        if (e.currentTarget.checkValidity()) {
            e.currentTarget.classList.remove("was-validated");
            setIsSubmitting(true);

            const url = "/api/Physician/SaveCCAttestations?ccBlockId=" + ccBlock.ccBlockId;

            abpnClient.post<CCBlock>(url, ccBlock.ccAttestations, authContext).then(result => {

                setCCBlock(result);
                setIsSubmitting(false);
                setIsEditingAttestations(false);
                setDisplaySuccessSaveAlert(true);

                setTimeout(() => {
                    setDisplaySuccessSaveAlert(false);
                }, 5000)

                setIsFormDirty(false);

                props.onSaveBlock(result);
            });
        }
    }

    const cancelEdit = () => {
        toggleEdit();
        setCCBlock(deepCopy(props.ccBlock));
    }

    const updateIsPIPClinicalChecked = () => {
        setIsPIPClincalChecked(!isPIPClinicalChecked);
        var attestation = ccBlock.ccAttestations.find(_ => _.appliedToYear === ccBlock.endYear && _.ccAttestationType === CCAttestationType.PIPClinical);

        if (attestation == null) {
            attestation = createNewAttestation(CCAttestationType.PIPClinical);
        }

        attestation.selfReportedCredits = selfAttestedPIPClinicalCredits === 1 ? 0 : 1;
        attestation.ccBlockId = ccBlock.ccBlockId;
        updateCCAttestations(attestation);
        setIsFormDirty(true);
    }

    const updateIsPIPFeedbackChecked = () => {
        setIsPIPFeedbackChecked(!isPIPFeedbackChecked);
        var attestation = ccBlock.ccAttestations.find(_ => _.appliedToYear === ccBlock.endYear && _.ccAttestationType === CCAttestationType.PIPFeedback);

        if (attestation == null) {
            attestation = createNewAttestation(CCAttestationType.PIPFeedback);
        }

        attestation.selfReportedCredits = selfAttestedPIPFeedbackCredits === 1 ? 0 : 1;
        attestation.ccBlockId = ccBlock.ccBlockId;
        updateCCAttestations(attestation);
        setIsFormDirty(true);
    }

    const updateIsPSChecked = () => {
         setIsPSChecked(!isPSChecked);
         var attestation = ccBlock.ccAttestations.find(_ => _.appliedToYear === ccBlock.endYear && _.ccAttestationType === CCAttestationType.PatientSafety);

        if (attestation == null) {
            attestation = createNewAttestation(CCAttestationType.PatientSafety);
        }

        attestation.selfReportedCredits = selfAttestedPSCredits === 1 ? 0 : 1;
        attestation.ccBlockId = ccBlock.ccBlockId;
        updateCCAttestations(attestation);
        setIsFormDirty(true);
    }

    const openApprovedActivities = () => {
        window.open("https://www.abpn.org/maintain-certification/abpn-approved-products-list/", "_blank")
    }

    const getPipRequirementMessage = (ccBlock: CCBlock) => {

        if (ccBlock.hasPipWaiver) return "Waived"
        if (isPipRequired) return "Not Met"
        return "Not Required"
    }

    const getDivBorderColor = ()=>
    {
        if(ccBlock.hasPassedAudit)
         return "mb-4 pp-div-CC-PassAudit"
         if(ccBlock.hasFailedAudit)
         return "mb-4 pp-div-CC-FailAudit"
         if(ccBlock.hasAudit)
         return "mb-4 pp-div-CC-HasAudit"

         return "mb-4 pp-secondary-grey"

    }

    const getLastUpdated = () => {
        var ccAttestations = ccBlock.ccAttestations
        var LastUpdated = new Date(Math.max(...ccAttestations.map(_ => new Date(_.updatedDate).getTime())));
        return LastUpdated.toLocaleDateString();
    }

    const totalWaivedAndEarnedSACreditsDisplay = () => {
        return totalWaivedAndEarnedSACredits > ccBlock.requiredSACredits ? ccBlock.requiredSACredits : totalWaivedAndEarnedSACredits;
    }

    const showOverageInfoIcons = () => {
        return totalWaivedAndEarnedSACredits > ccBlock.requiredSACredits;
    }

    return (
       <Card className={getDivBorderColor()}>
            {displaySuccessSaveAlert &&
                <Alert variant="pp-alert-success" >
                    <p>Successfully Saved and Attested</p>
                </Alert>
            }
            <Prompt
                when={isFormDirty}
                message='You have unsaved changes, are you sure you want to leave?'
            />
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder">
                    <span className="text-pp-primary">Continuing Certification Activity Requirements</span>
                </Card.Title>
                <Collapse >
                    <Fragment>
                        <Row className="mt-3">
                            <Col xs={12} md={12} lg={12} xl={12}>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group controlId="dueDate">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">
                                            The ABPN Continuing Certification Requirements Overview can be found<a className="pl-1 pp-font-size-smaller" href="https://abpn.org/continuing-certification/" target="_BLANK">here</a>.
                                            </Form.Label>
                                            <div className="pp-font-size-smaller">Click on the CC activity below to view a detailed descriptions of how to meet requirements.</div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row className="mt-3 align-items-start">
                            <Col xs={12} md={12} lg={12} xl={12}>
                                <Row className="align-items-start">
                                    <Col>
                                        <Form.Group controlId="dueDate">
                                            <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller">Activity Attestations Due By</Form.Label>
                                            <Form.Text>{getDateWithOffset(props.ccBlock.endDate)?.toLocaleDateString()}</Form.Text>
                                        </Form.Group>
                                        {!isEditingAttestations &&
                                            <Button variant="pp-primary" onClick={() => toggleEdit()} className="mt-2">Add and Attest</Button>
                                        }
                                    </Col>
                                    <Col className="d-flex flex-column justify-content-center">
                                        <ClinicalStatusRow ccBlock={ccBlock} updatePhysician={props.updatePhysician} physician={props.physician} editable={ClinicalStatusIsEditable} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col xs={12} xl={6}>
                                <Row>
                                    <Col xs={9}>
                                        <Form.Label className="font-weight-bolder mb-0 pp-font-size-smaller"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/self-assessment-sa/" target="_blank">Self-Assessment CME (SA-CME)</a></Form.Label>
                                        <OverlayTrigger placement={"right"}
                                            overlay={<Tooltip id="medicalLicense" bsPrefix="pp-tooltip">
                                            Self-Assessment CME (SA-CME) credits contribute to the overall number of CME Credits.  Any attested to SA CME credits over the {ccBlock.requiredSACredits} SA credits required will be applied to the required Category 1 CME credits.
                                            </Tooltip>}>
                                            <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                                        </OverlayTrigger>
                                        <ProgressBar variant="pp-secondary" now={percentSACredits} key={1} />
                                    </Col>
                                    <Col xs={3}>
                                        <div className="pp-font-size-smaller mt-4 text-nowrap">
                                            (<span className="font-weight-bolder">{totalWaivedAndEarnedSACreditsDisplay()}</span>/{ccBlock.requiredSACredits})
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={9}>
                                        <Form.Label className="font-weight-bolder mb-0 pp-font-size-smaller"><a href="https://abpn.org/maintain-certification/moc-activity-requirements/continuing-medical-education-cme/" target="_blank">CME</a></Form.Label>
                                        <OverlayTrigger placement={"right"}
                                            overlay={<Tooltip id="cmeCredits1" bsPrefix="pp-tooltip">
                                            This CME count includes any Self-Assessment CMEs self reported, verified, or waived over the minimum {ccBlock.requiredSACredits} SA CMEs required in the reported total.
                                            </Tooltip>}>
                                            <i hidden={!showOverageInfoIcons()} className="las la-info-circle ml-1 text-pp-secondary"></i>
                                        </OverlayTrigger>
                                        <ProgressBar variant="pp-secondary" now={percentCMECredits} key={2} />
                                    </Col>
                                    <Col xs={3}>
                                        <div className="pp-font-size-smaller mt-4 text-nowrap">
                                            (<span className="font-weight-bolder">{totalWaivedAndEarnedCMECredits + overageOfSACredits}</span>/{ccBlock.requiredCMECredits})
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={12} xl={6}>
                                <Row>
                                    <Col xs={12}>
                                        <div className="font-weight-bolder mb-0 pp-font-size-smaller">
                                            <a href="https://abpn.org/maintain-certification/moc-activity-requirements/improvement-in-medical-practice-pip/" target="_blank">Performance in Practice (PIP)</a>
                                        </div>
                                        {ccBlock.hasPipClinicalAttestation || ccBlock.hasPipFeedbackAttestation || isPIPClinicalVerifiedChecked || isPIPFeedbackVerifiedChecked ?
                                            <div className="mb-4"><i className="font-weight-bolder las la-check pp-icon-secondary position-absolute"></i></div>
                                            : <span className="pp-font-size-smaller">{getPipRequirementMessage(ccBlock)}</span>}
                                    </Col>
                                    <Col xs={12}>
                                        <PatientSafetlyRequirementsColumn ccBlock={ccBlock} isPSVerified={isPSVerified} isPhysicianPatientSafetyRequired={props.isPhysicianPatientSafetyRequired}/>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row>
                            {ccBlock.isActivityRequirementsCompleted && <Col xs={12}><div className="ml-3 font-weight-bolder">Congratulations! You Have Completed All Activity Requirements</div></Col>}
                            {isInAbcc && ccBlock.endYear == moment().year() && <Col xs={12}><Alert className="m-0 font-weight-bolder" variant="pp-alert-primary" > 16 Self-Assessment CME (SA-CME) credits will be waived for successful completion of the required ABCC article exams. This waiver will be granted by ABPN and will be reflected in your Portal 1-2 days after successful completion of your required article exams for this ABCC block.</Alert></Col>}
                        </Row>

                        {isEditingAttestations &&
                            <Row className="mt-3">
                                <Col xs={12} lg={12}>
                                    <Row>
                                        <Col xs={12}>
                                            <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller text-pp-primary">
                                                Activity Attestations
                                            </Card.Text>
                                        </Col>
                                    </Row>
                                    <Form noValidate onSubmit={(e) => saveAllCCAttestations(e)}>
                                        <Row>
                                            <Col xs={12}>
                                                <Table responsive className="pp-activity-attestation-table">
                                                    <thead>
                                                        <tr>
                                                            <th></th>
                                                            <th style={{width: '250px'}}>
                                                                <Form.Label className="mb-1 pp-font-size-smaller pp-table-header">Enter Total Completed
                                                                    <OverlayTrigger
                                                                        placement={"right"}
                                                                        overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                                                            These activities are not verified or waived by ABPN.
                                                                        </Tooltip>}>
                                                                        <i className="las la-info-circle ml-1"></i>
                                                                    </OverlayTrigger>
                                                                </Form.Label>
                                                                {ccBlock.ccAttestations.length > 0 && <Form.Label className="pl-1"><i>Last Updated {getLastUpdated()}</i></Form.Label>}
                                                            </th>
                                                            <th>
                                                                <Form.Label className="mb-1 pp-font-size-smaller pp-table-header">Verified
                                                                    <OverlayTrigger
                                                                        placement={"right"}
                                                                        overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                                                            Records of completed CC credits from select professional organizations are reported directly to ABPN within approximately three months of the activity completion. The verified credits are reflected in the CC Activity Attestations section.  In the event of an audit no further documentation is required from a diplomate for verified activities.
                                                                        </Tooltip>}>
                                                                        <i className="las la-info-circle ml-1"></i>
                                                                    </OverlayTrigger>
                                                                </Form.Label>
                                                            </th>
                                                            <th>
                                                                <Form.Label className="mb-1 pp-font-size-smaller pp-table-header">Waived
                                                                    <OverlayTrigger
                                                                        placement={"right"}
                                                                        overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                                                            Activity credits that have been waived by the ABPN for Fellowship, Article Pathway, Exam Credit, and/or non-CME SA activities.
                                                                            <br /><br />
                                                                            If you have successfully participated in the ABCC pathway, you will still need to complete 8 SA-CME credits from an external activity on the ABPN approved CC activities list.
                                                                            {ccBlock?.ccAppliedWaivers?.length > 0 &&
                                                                                <Fragment>
                                                                                    <br /><br />
                                                                                    Your activity waiver(s):<br />
                                                                                    {ccBlock.ccAppliedWaivers.map(_ =>
                                                                                        <Fragment>
                                                                                            {_.waiverTypeDescription} ({_.cmeCredits > 0 ? `${_.cmeCredits} CME ` : null}{_.saCredits > 0 ? `${_.saCredits} SA` : null}) <br />
                                                                                        </Fragment>
                                                                                    )}
                                                                                </Fragment>

                                                                            }

                                                                        </Tooltip>}>
                                                                        <i className="las la-info-circle ml-1"></i>
                                                                    </OverlayTrigger>
                                                                </Form.Label>
                                                            </th>
                                                            <th>
                                                                <Form.Label className="mb-1 pp-font-size-smaller pp-table-header">Total</Form.Label>
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>
                                                                <Form.Label className="mb-1 pp-font-size-smaller">Self Assessment CME</Form.Label>
                                                            </td>
                                                            <td >
                                                                <div className="mb-2 mr-4">
                                                                    <Form.Control
                                                                        className="pp-required-input"
                                                                        type="number"
                                                                        required
                                                                        min={0}
                                                                        step="any"
                                                                        disabled={props.isReadOnly}
                                                                        onChange={(e) => updateSelfReportedSACME(parseFloat(e.target.value))}
                                                                        value={ccBlock.totalSAAttestationCredits} />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller mr-4">{ccBlock.totalVerifiedSACredits}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller mr-4">{ccBlock.totalWaivedSACredits}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller mr-4">{totalWaivedAndEarnedSACreditsDisplay()}/{ccBlock.requiredSACredits}</Form.Label>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <Form.Label className="mb-1 pp-font-size-smaller">CME</Form.Label>
                                                            </td>
                                                            <td>
                                                                <div className="mb-2 mr-4">
                                                                    <Form.Control
                                                                        className="pp-required-input"
                                                                        type="number"
                                                                        required
                                                                        min={0}
                                                                        step="any"
                                                                        disabled={props.isReadOnly}
                                                                        onChange={(e) => updateSelfReportedCME(parseFloat(e.target.value))}
                                                                        value={ccBlock.totalCMEAttestationCredits} />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller mr-4">{ccBlock.totalVerifiedCMECredits}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller mr-4">{ccBlock.totalWaivedCMECredits}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label className="font-weight-bolder mb-1 pp-font-size-smaller mr-4">{totalWaivedAndEarnedCMECredits + overageOfSACredits}/{ccBlock.requiredCMECredits}
                                                                    <OverlayTrigger
                                                                        placement={"right"}
                                                                        overlay={<Tooltip id="cmeCredits2" bsPrefix="pp-tooltip">
                                                                            The CME count includes any Self-Assessment CMEs self reported, verified, or waived over the minimum {ccBlock.requiredSACredits} SA CMEs required in the reported total.
                                                                        </Tooltip>}>
                                                                        <i hidden={!showOverageInfoIcons()} className="las la-info-circle ml-1"></i>
                                                                    </OverlayTrigger>
                                                                </Form.Label>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <Form.Label className="mb-1 pp-font-size-smaller pp-side-icon">PIP - Clinical
                                                                    <OverlayTrigger
                                                                        placement={"right"}
                                                                        overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                                                            Only one PIP (Clinical or Feedback) is required.
                                                                        </Tooltip>}>
                                                                        <i className="las la-info-circle ml-1"></i>
                                                                    </OverlayTrigger>
                                                                </Form.Label>
                                                            </td>
                                                            <td>
                                                                {!ccBlock.hasPipWaiver ?
                                                                    <Form.Check type="checkbox">
                                                                        <Form.Check.Input type="checkbox" checked={isPIPClinicalChecked}
                                                                            disabled={isPIPDisabled || props.isReadOnly}
                                                                            onChange={() => updateIsPIPClinicalChecked()} />
                                                                    </Form.Check>
                                                                    : "-"
                                                                }
                                                            </td>
                                                            <td>
                                                                <Form.Label>{isPIPClinicalVerifiedChecked ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label>{ccBlock.hasPipWaiver ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label>{(isPIPClinicalVerifiedChecked || isPIPClinicalChecked || ccBlock.hasPipWaiver) ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <Form.Label className="mb-1 pp-font-size-smaller pp-side-icon">PIP - Feedback
                                                                    <OverlayTrigger
                                                                        placement={"right"}
                                                                        overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                                                            Only one PIP (Clinical or Feedback) is required.
                                                                        </Tooltip>}>
                                                                        <i className="las la-info-circle ml-1"></i>
                                                                    </OverlayTrigger>
                                                                </Form.Label>
                                                            </td>
                                                            <td>
                                                                {!ccBlock.hasPipWaiver ?
                                                                    <Form.Check type="checkbox">
                                                                        <Form.Check.Input type="checkbox" checked={isPIPFeedbackChecked}
                                                                            disabled={isPIPDisabled || props.isReadOnly}
                                                                            onChange={() => updateIsPIPFeedbackChecked()} />
                                                                    </Form.Check>
                                                                    : "-"
                                                                }
                                                            </td>
                                                            <td>
                                                                <Form.Label>{isPIPFeedbackVerifiedChecked ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label>{ccBlock.hasPipWaiver ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
                                                            </td>
                                                            <td>
                                                                <Form.Label>{(isPIPFeedbackVerifiedChecked || isPIPFeedbackChecked || ccBlock.hasPipWaiver) ? <i className="font-weight-bolder las la-check"></i> : "-"}</Form.Label>
                                                            </td>
                                                        </tr>
                                                        <PatientSafetyActivityAttestationRow ccBlock={ccBlock} isPSChecked={isPSChecked} isPSDisabled={isPSDisabled} isPSVerified={isPSVerified} isPhysicianPatientSafetyRequired={props.isPhysicianPatientSafetyRequired} isReadOnly={props.isReadOnly} updateIsPSChecked={updateIsPSChecked}/>
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        </Row>

                                        <Row className="mt-3">
                                            <Col xs={12} lg={6}>
                                                {isSubmitting ?
                                                    <AbpnSpinner />
                                                    :
                                                    <Button type="submit" variant="pp-primary" disabled={props.isReadOnly} className="m-1 w-100">Save and Attest</Button>
                                                }
                                            </Col>
                                            <Col xs={12} lg={6}>
                                                <Button variant="outline-dark" className="m-1 w-100" onClick={() => cancelEdit()}>Cancel</Button>
                                            </Col>
                                        </Row>
                                    </Form>

                                    <Row className="mt-3">
                                        <Col xs={12}>
                                            <Link to="/ManageActivities"><Button variant="outline-pp-primary" className="m-1 w-100">View and Manage Activity Records</Button></Link>
                                        </Col>
                                        <Col xs={12}>
                                            <Button variant="outline-pp-primary" className="m-1 w-100" onClick={() => openApprovedActivities()}>View ABPN Approved Activities List <i className="text-pp-primary las la-external-link-alt"></i></Button>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>}
                    </Fragment>

                </Collapse>
            </Card.Body>
        </Card>
    );

}